import { Component, computed, inject, input, OnInit, signal, WritableSignal } from '@angular/core';
import { select, Store } from '@ngxs/store';
import {
  ClearAllSelectedVehiclesUuid,
  ConfigurationState,
  dealerFilterConfigurationMap,
  getBucketsForAggregatedTerm,
  InventoryManagementBidsFilter,
  InventoryManagementDealerFilter,
  InventoryManagementRepostedStatus,
  InventoryManagementState,
  LaunchDarklyService,
  OrganizationState,
  SearchResultsAggregationBucket,
  StatusFilter
} from 'remarketing-angular-library';
import { translateObjectSignal } from '@jsverse/transloco';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { CommonModule } from '@angular/common';
import {
  DropdownSelectComponent,
  InputElementMetaData,
  RadioButtonGroupComponent,
  TrapFocusComponent
} from 'remarketing-ui-components-lib';
import { DateRangeComponent } from '../date-range/date-range.component';
import { SearchFilterSectionService } from '../../services/search-filter-section/search-filter-section.service';
import {
  BidsOffersFilters,
  dealerFilterMap,
  FilterSectionTypes,
  groundedFilterMap,
  GroundedFilters,
  OfferFilters,
  QuestionFilters
} from '../../enums/inventory-management.enum';
import { SearchFilterService } from '../../services/search-filter/search-filter.service';
import { VehicleTypeSelectComponent } from '../../../shared/components/vehicle-type-select/vehicle-type-select.component';
import { AvisVehicleType } from '../../../app/enums/vehicleInfo.enum';
import { hasSameValues } from '../../util/util';
import { DealerFilterComponent } from '../dealer-filter/dealer-filter.component';
import { SearchFilterSectionComponent } from './search-filter-section/search-filter-section.component';
import { AccountManagerEmployeeAggregation } from 'src/inventory-management/enums/account-manager.enum';
import { AccountManagerDealerFilterComponent } from "../account-manager/account-manager-dealer-filter/account-manager-dealer-filter.component";
import { BidsFilterComponent } from "./bids-filter/bids-filter.component";
import { AccountManagerSharedVehiclesFilterComponent } from "../account-manager/account-manager-shared-vehicles-filter/account-manager-shared-vehicles-filter.component";
import { SharedVehiclesFilterComponent } from './shared-vehicles-filter/shared-vehicles-filter.component';

@Component({
  selector: 'app-search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.scss'],
  imports: [
    CommonModule,
    RadioButtonGroupComponent,
    SearchFilterSectionComponent,
    SearchFilterSectionComponent,
    VehicleTypeSelectComponent,
    TrapFocusComponent,
    DropdownSelectComponent,
    DealerFilterComponent,
    SearchFilterSectionComponent,
    DateRangeComponent,
    AccountManagerDealerFilterComponent,
    BidsFilterComponent,
    AccountManagerSharedVehiclesFilterComponent,
    SharedVehiclesFilterComponent
  ]
})
export class SearchFilterComponent extends SaComponent implements OnInit {
  // Inputs
  isMobile = input<boolean>(false);

  // Signals
  protected readonly inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);
  private readonly inventoryManagementFilterStatus$ = select(InventoryManagementState.currentStatus);
  private readonly inventoryManagementDealerFilterConfiguration$ = select(
    InventoryManagementState.dealerFilterConfiguration
  );
  protected readonly filterByIsv$ = select(InventoryManagementState.filterByInService);
  protected readonly sellerDealerName$ = select(OrganizationState.oeName);
  private readonly translations$ = translateObjectSignal(['sellModule.manageInventory.searchFilter']);

  // Computed Signals
  protected readonly searchFilterTranslations = computed(() => {
    const translate = this.translations$();
    return {
      textFilters: translate[0]?.textFilters,
      textActiveVehicles: translate[0]?.textActiveVehicles,
      textSoldVehicles: translate[0]?.textSoldVehicles,
      textHoldVehicles: translate[0]?.textHoldVehicles,
      textSharedVehicles:
        this.isInServiceVehiclesEnabled && !this.filterByIsv$()
          ? translate[0]?.textCustomOfferVehicles
          : translate[0]?.textSharedVehicles,
      labelAllOffersTo:
        this.isInServiceVehiclesEnabled && !this.filterByIsv$()
          ? translate[0]?.labelAllCustomOffersTo
          : translate[0]?.labelAllSharedListOffersTo,
      textRemovedVehicles: translate[0]?.textRemovedVehicles,
      textReservedVehicles: translate[0]?.textReservedVehicles,
      textRemovalType: translate[0]?.textRemovalType,
      textAllVehicles: translate[0]?.textAllVehicles,
      textAll: translate[0]?.textAll,
      titleGroundingStatus: translate[0]?.titleGroundingStatus,
      titleGrounded: translate[0]?.titleGrounded,
      titleNotGrounded: translate[0]?.titleNotGrounded,
      titleSharedWith: translate[0]?.titleSharedWith,
      titleInvoicedTo: translate[0]?.titleInvoicedTo,
      titleOffers: translate[0]?.titleOffers,
      titleQuestions: translate[0]?.titleQuestions,
      titleBidsOffers: translate[0]?.titleBidsOffers,
      labelBids: translate[0]?.labelBids,
      labelSoldDate: translate[0]?.labelSoldDate,
      labelNotReposted: translate[0]?.labelNotReposted,
      labelReposted: translate[0]?.labelReposted,
      labelDontFilter: translate[0]?.labelDontFilter,
      labelOutstandingOffers: translate[0]?.labelOutstandingOffers,
      labelSellerCountered: translate[0]?.labelSellerCountered,
      labelAllSharedListOffersTo: translate[0]?.labelAllSharedListOffersTo,
      labelDontQuestion: translate[0]?.labelDontQuestion,
      labelOutstandingQuestions: translate[0]?.labelOutstandingQuestions,
      labelAllQuestion: translate[0]?.labelAllQuestion,
      labelWinningBidsOffers: translate[0]?.labelWinningBidsOffers,
      labelLosingBidsOffers: translate[0]?.labelLosingBidsOffers,
      labelShowAll: translate[0]?.labelShowAll,
      labelReserveInformation: translate[0]?.labelReserveInformation,
      labelDealership: translate[0]?.labelDealership,
      labelAvisRep: translate[0]?.labelAvisRep,
      labelAllOffer: translate[0]?.labelAllOffer,
      labelSelectAll: translate[0]?.labelSelectAll,
      labelShowAllDealerCount: translate[0]?.labelShowAllDealerCount,
      labelDealers: translate[0]?.labelDealers,
      labelSellers: translate[0]?.labelSellers,
      labelShowLess: translate[0]?.labelShowLess,
      labelShowMore: translate[0]?.labelShowMore,
      labelTenant: translate[0]?.labelTenant,
      labelAllRecipients: translate[0]?.labelAllRecipients,
      labelAllLists: translate[0]?.labelAllList,
      textNotAvailable: translate[0].textNotAvailable,
      radioButtonGroupBids: {
        all: translate[0]?.radioBtnGrpBids?.all,
        activeBids: translate[0]?.radioBtnGrpBids?.activeBids,
        noBids: translate[0]?.radioBtnGrpBids?.noBids
      }
    };
  });

  protected readonly tenantList$ = computed(() => {
    // set default option
    const tenantList = [
      {
        id: 'active-tenant-filter',
        class: 'custom-control-input',
        groupName: 'Tenant',
        name: 'All',
        value: 'All',
        dataTrackname: 'Active-All',
        ariaLabel: 'All',
        isChecked: this.inventoryManagementSearchCriteria$().tenantUuid === undefined
      }
    ] as InputElementMetaData[];

    // get additional tenants from the response metadata
    const tenants = this.searchFilterService.getTenantList;
    tenants.forEach(tenant => {
      tenantList.push({
        id: 'active-tenant-filter',
        class: 'custom-control-input',
        groupName: 'Tenant',
        name: tenant.name,
        value: tenant.value,
        dataTrackname: tenant.name,
        ariaLabel: tenant.name,
        isChecked: this.inventoryManagementSearchCriteria$().tenantUuid === tenant.value
      } as InputElementMetaData);
    });

    return tenantList;
  });

  protected readonly reservedByUsersDropdown$ = computed(() => {
    const reservedByUsers = this.searchFilterService.getReservedByUsers;
    const currentReservedByUsers = this.store.selectSnapshot(InventoryManagementState.searchCriteria).reservedByUsers;
    if (reservedByUsers?.length > 0) {
      return [
        {
          options: [
            {
              name: 'All',
              value: 'All',
              selected: currentReservedByUsers.length === 0
            },
            ...reservedByUsers.map(user => {
              return {
                name: user.name,
                value: user.value,
                selected: currentReservedByUsers.length === 0 ? false : currentReservedByUsers.includes(user.value)
              };
            })
          ]
        }
      ];
    }

    return [
      {
        options: [
          {
            name: 'All',
            value: 'All',
            selected: true
          }
        ]
      }
    ];
  });

  protected readonly dealershipDropdown$ = computed(() => {
    const reservedForDealers = this.searchFilterService.getReservedForDealers;
    const currentReservedForDealers = this.store.selectSnapshot(InventoryManagementState.searchCriteria)
      .reservedForDealers;
    if (reservedForDealers?.length > 0) {
      return [
        {
          options: [
            {
              name: 'All',
              value: 'All',
              selected: currentReservedForDealers.length === 0
            },
            ...reservedForDealers.map(dealer => {
              return {
                name: dealer.name,
                value: dealer.value,
                selected:
                  currentReservedForDealers.length === 0 ? false : currentReservedForDealers.includes(dealer.value)
              };
            })
          ]
        }
      ];
    }

    return [
      {
        options: [
          {
            name: 'All',
            value: 'All',
            selected: true
          }
        ]
      }
    ];
  });

  protected readonly showAllDealersText$ = computed(() => {
    return `${this.searchFilterTranslations().labelShowAllDealerCount} ${this.offersDealerList.length} ${
      this.searchFilterTranslations().labelDealers
    }`;
  });

  protected readonly showAllQuestionsText$ = computed(() => {
    return `${this.searchFilterTranslations().labelShowAllDealerCount} ${this.questionsDealerList.length} ${
      this.searchFilterTranslations().labelDealers
    }`;
  });

  protected readonly showAllOfferListCreatorsText$ = computed(() => {
    return !this.isShowAll(OfferFilters.OFFERS_BY_DEALER)
      ? `${this.searchFilterTranslations().labelShowAllDealerCount} ${this.offerListCreators.length} ${
          this.searchFilterTranslations().labelDealers
        }`
      : this.searchFilterTranslations().labelShowLess;
  });

  protected readonly sellerCounterOffersDealerList$ = computed(() => {
    const dealerList = this.searchFilterService.getDealerList(OfferFilters.DEALERS_WITH_COUNTER_OFFERS);
    if (dealerList) {
      return dealerList.sort((a, b) => a.name.localeCompare(b.name));
    }
    return dealerList;
  });

  private readonly isSharedListFilterSelected$ = computed(() => {
    return this.inventoryManagementFilterStatus$() === StatusFilter.SHARED_VEHICLES;
  });

  // Attributes
  protected readonly statusFilters = StatusFilter;
  protected readonly filterSectionTypes = FilterSectionTypes;
  protected readonly offerFilters = OfferFilters;
  protected readonly questionFilters = QuestionFilters;
  protected readonly repostedFilters = InventoryManagementRepostedStatus;
  protected readonly bidsOffersFilters = BidsOffersFilters;
  protected readonly groundedFilters = GroundedFilters;
  protected readonly accountManagerEmployeeAggregation = AccountManagerEmployeeAggregation;
  private readonly store: Store;
  private readonly launchDarklyService: LaunchDarklyService;
  private readonly searchFilterSectionService: SearchFilterSectionService;
  private readonly searchFilterService: SearchFilterService;
  protected analyticsRadioOption: string;

  constructor() {
    super();
    this.store = inject(Store);
    this.launchDarklyService = inject(LaunchDarklyService);
    this.searchFilterSectionService = inject(SearchFilterSectionService);
    this.searchFilterService = inject(SearchFilterService);
  }

  // Getters
  get isSoldInventoryFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-soldInventoryOptionEnabled', false);
  }

  get isReservedFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sim-reserved-filterEnabled', false);
  }

  get isSellReservationFiltersEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-reservationFiltersEnabled', false);
  }

  get isOfferListFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('offerList-seller-createEnabled', false);
  }

  get isInServiceVehiclesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get isSellHoldFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-holdFilterEnabled', false);
  }

  get isSellUpdateInventoryFeatureEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
  }

  get isSellRemovedFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-removedFilterEnabled', false);
  }

  get isSellAllFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-allFilterEnabled', false);
  }

  get isSellerCounteredFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sim-sellerCounteredFilterEnabled', false);
  }

  get isAdmin(): boolean {
    return this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  isSellRemovalTypeFilterEnabled(): boolean {
    return !this.launchDarklyService.getFeatureFlag('sell-removalTypeFilterEnabled', false);
  }

  get isSoldVehiclesFilterSelected(): boolean {
    return this.inventoryManagementFilterStatus$() === StatusFilter.SOLD_VEHICLES;
  }

  get isActiveVehiclesFilterSelected(): boolean {
    return this.inventoryManagementFilterStatus$() === StatusFilter.ACTIVE_VEHICLES;
  }

  get isRemoveVehiclesFilterSelected(): boolean {
    return this.inventoryManagementFilterStatus$() === StatusFilter.REMOVED_VEHICLES;
  }

  get showBidLeftRailFilters(): boolean {
    return !this.filterByIsv$() && this.launchDarklyService.getFeatureFlag('sell-bidFilterEnabled', true);
  }

  removedVehiclesSA(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-removalTypeFilterEnabled', false);
  }

  get isRemovalFilterHideShow(): boolean {
    return this.inventoryManagementFilterStatus$() === StatusFilter.REMOVED_VEHICLES && this.removedVehiclesSA();
  }

  get showRemovalTypeLeftRailFilters(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-removalTypeFilterEnabled', true);
  }

  get showOffersLeftRailFilters(): boolean {
    return !this.filterByIsv$() && this.launchDarklyService.getFeatureFlag('sell-offerFilterEnabled', true);
  }

  get getOpenFilterSection() {
    return this.searchFilterSectionService.getOpenFilterSection();
  }

  get isSharedVehiclesFilterSelected(): boolean {
    return this.inventoryManagementFilterStatus$() === StatusFilter.SHARED_VEHICLES;
  }

  get isShowBuyingDealershipName(): boolean {
    return this.launchDarklyService.getFeatureFlag('showBuyingDealershipName', false);
  }

  get showQuestionsLeftRailFilters(): boolean {
    return !this.filterByIsv$() && this.launchDarklyService.getFeatureFlag('sell-questionsFilterEnabled', true);
  }

  get showSoldBidsOffersLeftRailFilters(): boolean {
    return !this.filterByIsv$() && this.launchDarklyService.getFeatureFlag('sell-bidsOffersFiltersEnabled', true);
  }

  get isSimEmployeeFilterEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sim-employeeFilterEnabled', false);
  }

  get offersDealerList(): SearchResultsAggregationBucket[] {
    return this.searchFilterService.getDealerList(OfferFilters.OFFERS_BY_DEALER);
  }

  get questionsDealerList(): SearchResultsAggregationBucket[] {
    return this.searchFilterService.getDealerList(QuestionFilters.QUESTIONS_BY_DEALER);
  }

  get winningBidsOffersDealerList(): SearchResultsAggregationBucket[] {
    return this.searchFilterService.getDealerList(BidsOffersFilters.WINNING_BIDS_OFFERS);
  }

  get losingBidsOffersDealerList(): SearchResultsAggregationBucket[] {
    return this.searchFilterService.getDealerList(BidsOffersFilters.LOSING_BIDS_OFFERS);
  }

  get offerListCreators(): SearchResultsAggregationBucket[] {
    return this.searchFilterService.getOfferListCreators;
  }

  get isAccountManagerFilterEnabled(): boolean {
    return (
      this.isSimEmployeeFilterEnabled &&
      this.inventoryManagementSearchCriteria$()?.filterByEmployee &&
      this.inventoryManagementSearchCriteria$()?.filterByEmployee?.length > 0
    );
  }

  get isReserveFilterEnabled(): boolean {
    return (
      this.isSellReservationFiltersEnabled &&
      (this.inventoryManagementFilterStatus$() === StatusFilter.RESERVED_VEHICLES ||
        this.inventoryManagementFilterStatus$() === StatusFilter.SOLD_VEHICLES) &&
      this.filterByIsv$()
    );
  }

  get showTenantFilter() {
    return (
      this.isAdmin ||
      (!this.filterByIsv$() &&
        this.isSellRemovalTypeFilterEnabled() &&
        (this.isSoldVehiclesFilterSelected || this.isActiveVehiclesFilterSelected))
    );
  }

  get isGroundingStatusFilterHideShow(): boolean {
    return (
      this.filterByIsv$() &&
      this.launchDarklyService.getFeatureFlag('sell-groundedFilterEnabled', false) &&
      (this.inventoryManagementFilterStatus$() === StatusFilter.ACTIVE_VEHICLES ||
        this.inventoryManagementFilterStatus$() === StatusFilter.RESERVED_VEHICLES)
    );
  }

  get isCustomOffersEnabled() {
    return this.launchDarklyService.getFeatureFlag('offerlist-avisCustomOfferEnabled', false);
  }

  get isSharedVehicleFilterEnabled() {
    if (this.isInServiceVehiclesEnabled && !this.filterByIsv$()) {
      return this.isOfferListFeatureEnabled && this.isCustomOffersEnabled;
    }
    return this.isOfferListFeatureEnabled;
  }

  get getSharedVehiclesSectionTitle(): string {
    return !this.filterByIsv$()
      ? this.searchFilterTranslations().titleSharedWith
      : this.searchFilterTranslations().titleInvoicedTo;
  }

  private setAnalyticsVariables(): void {
    this.analyticsRadioOption = this.isInServiceVehiclesEnabled ? 'Invoiced' : 'Shared';
  }

  ngOnInit(): void {
    this.setAnalyticsVariables();
  }

  setStatusFilter(statusFilter: StatusFilter): void {
    this.store.dispatch(new ClearAllSelectedVehiclesUuid());

    if (!this.isStatusFilterChecked(statusFilter)) {
      this.searchFilterService.setFilterStatus(statusFilter);
    }
  }

  isStatusFilterChecked(statusFilter: StatusFilter): boolean {
    return this.inventoryManagementFilterStatus$() === statusFilter;
  }

  setBidFilter(bidFilter: InventoryManagementBidsFilter): void {
    if (!this.isBidFilterSelected(bidFilter)) {
      this.searchFilterService.setBidsFilter(bidFilter);
    }
  }

  isBidFilterSelected(bidFilter: InventoryManagementBidsFilter): boolean {
    return bidFilter === this.inventoryManagementSearchCriteria$().filterByBids;
  }

  setTenantFilter(tenantUuid: string): void {
    if (!this.isTenantFilterSelected(tenantUuid)) {
      if (tenantUuid === 'All') {
        this.searchFilterService.setTenantUuid(undefined);
      } else {
        this.searchFilterService.setTenantUuid(tenantUuid);
      }
    }
  }

  isTenantFilterSelected(tenantUuid: string): boolean {
    return tenantUuid === this.inventoryManagementSearchCriteria$().tenantUuid;
  }

  setRepostedFilter(filterByReposted: InventoryManagementRepostedStatus): void {
    if (!this.isRepostedFilterChecked(filterByReposted)) {
      this.searchFilterService.setRepostedFilter(filterByReposted);
    }
  }

  isRepostedFilterChecked(repostedFilter: InventoryManagementRepostedStatus): boolean {
    return repostedFilter === this.inventoryManagementSearchCriteria$().filterByReposted;
  }

  setSearchFilterSection(filterSectionType: FilterSectionTypes): void {
    this.searchFilterSectionService.setOpenFilterSection(filterSectionType);
  }

  setDealerFilter(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters) {
    if (!this.isDealerFilterChecked(dealerFilter)) {
      this.searchFilterService.setDealerFilter(dealerFilter);
    }
  }

  isDealerFilterChecked(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters) {
    const noneFilterMap = {
      [QuestionFilters.NONE]: [
        InventoryManagementDealerFilter.QUESTIONS_BY_DEALER,
        InventoryManagementDealerFilter.QUESTIONS_BY_ANY_DEALER
      ],
      [OfferFilters.NONE]: [
        InventoryManagementDealerFilter.OFFERS_BY_ANY_DEALER,
        InventoryManagementDealerFilter.OFFERS_BY_DEALER,
        InventoryManagementDealerFilter.DEALERS_WITH_COUNTER_OFFERS
      ],
      [BidsOffersFilters.ALL]: [
        InventoryManagementDealerFilter.WON_BY_DEALER,
        InventoryManagementDealerFilter.BIDS_OFFERS_BY_DEALER
      ]
    };

    const currentFilterByDealer = this.inventoryManagementSearchCriteria$().filterByDealer;

    // if the filter is set to none, only check the valid filter options for that type instead of all of them
    if (dealerFilter in noneFilterMap) {
      return !noneFilterMap[dealerFilter].includes(currentFilterByDealer);
    }

    // Default case
    return dealerFilterMap[dealerFilter] === currentFilterByDealer;
  }

  isGroundedFilterChecked(groundedFilter: GroundedFilters) {
    return groundedFilterMap[groundedFilter] === this.inventoryManagementSearchCriteria$().grounded;
  }

  setGroundedFilter(groundedFilter: GroundedFilters) {
    if (!this.isGroundedFilterChecked(groundedFilter)) {
      this.searchFilterService.setGroundedFilter(groundedFilter);
    }
  }

  selectVehicleType($event: AvisVehicleType) {
    const isIsv = $event !== AvisVehicleType.AVIS_DIRECT;
    if (this.filterByIsv$() !== isIsv) {
      this.setFilterByInService(isIsv);
    }
  }

  /* Used to search Avis Direct or In-Service Vehicles */
  setFilterByInService(isIsv: boolean) {
    const currentStatus = this.inventoryManagementFilterStatus$();
    // Any filters that exist for ISV but shouldn't exist for non-ISV need to be added here.
    if (
      (!isIsv && currentStatus === StatusFilter.RESERVED_VEHICLES) ||
      (isIsv && currentStatus === StatusFilter.HOLD_VEHICLES)
    ) {
      this.searchFilterService.setInService(isIsv, StatusFilter.ACTIVE_VEHICLES);
    } else {
      this.searchFilterService.setInService(isIsv, currentStatus);
    }

    this.searchFilterSectionService.resetFilterSection();
    this.searchFilterSectionService.setOpenFilterSection(FilterSectionTypes.GROUNDED);
  }

  selectReservedByUsersDropdown(event: any) {
    const reservedByUserValue = event.target?.dataset?.value;
    const currentReservedByUsers = this.inventoryManagementSearchCriteria$().reservedByUsers;

    // Early return if no value or value hasn't changed
    if (
      !reservedByUserValue ||
      (reservedByUserValue === 'All' && currentReservedByUsers.length === 0) ||
      (reservedByUserValue !== 'All' && currentReservedByUsers?.includes(reservedByUserValue))
    ) {
      return;
    }

    const newValue = reservedByUserValue === 'All' ? [] : reservedByUserValue;
    this.searchFilterService.setReservedByUsers(newValue);
  }

  selectDealershipDropdown(event: any) {
    const reservedForDealerValue = event.target?.dataset?.value;
    const currentReservedForDealers = this.inventoryManagementSearchCriteria$().reservedForDealers;

    // Early return if no value or value hasn't changed
    if (
      !reservedForDealerValue ||
      (reservedForDealerValue === 'All' && currentReservedForDealers.length === 0) ||
      (reservedForDealerValue !== 'All' && currentReservedForDealers?.includes(reservedForDealerValue))
    ) {
      return;
    }

    const newValue = reservedForDealerValue === 'All' ? [] : [reservedForDealerValue];
    this.searchFilterService.setReservedForDealers(newValue);
  }

  toggleSelectAll(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters) {
    this.searchFilterService.toggleSelectAll(dealerFilter);
  }

  isSelectAllChecked(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters) {
    const currentSelectAllStatus = this.store.selectSnapshot(InventoryManagementState.dealerFilterConfiguration);

    if (dealerFilter === OfferFilters.OFFERS_BY_DEALER && this.isSharedListFilterSelected$()) {
      return currentSelectAllStatus.offerListCreators.isSelectAllChecked;
    }

    return currentSelectAllStatus[dealerFilterConfigurationMap[dealerFilterMap[dealerFilter]]].isSelectAllChecked;
  }

  toggleShowAll(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters) {
    this.searchFilterService.toggleShowAll(dealerFilter);
  }

  isShowAll(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters) {
    const currentShowAllStatus = this.inventoryManagementDealerFilterConfiguration$();

    if (dealerFilter === OfferFilters.OFFERS_BY_DEALER && this.isSharedListFilterSelected$()) {
      return currentShowAllStatus.offerListCreators.isShowAll;
    }

    return currentShowAllStatus[dealerFilterConfigurationMap[dealerFilterMap[dealerFilter]]].isShowAll;
  }

  // @TODO: find a way to modify this into a computed signal for better performance
  // A dealer is only visually selected when its UUID is explicitly present in the search criteria.
  // A null criteria (meaning "all dealers" to the backend) is treated as visually unchecked.
  isDealerSelected(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters, dealerUuid: string): boolean {
    if (dealerFilter === OfferFilters.OFFERS_BY_DEALER && this.isSharedListFilterSelected$()) {
      return this.inventoryManagementSearchCriteria$().offerListCreators?.length
        ? this.inventoryManagementSearchCriteria$().offerListCreators.includes(dealerUuid)
        : false;
    }

    const criteriaField = this.inventoryManagementSearchCriteria$()[
      dealerFilterConfigurationMap[dealerFilterMap[dealerFilter]]
    ];

    return criteriaField?.length
      ? criteriaField.includes(dealerUuid)
      : false;
  }

  toggleDealer(dealerFilter: OfferFilters | QuestionFilters | BidsOffersFilters, dealerUuid: string) {
    this.searchFilterService.toggleDealer(dealerFilter, dealerUuid);
  }

  dealerById(index: number, dealer: any) {
    return dealer.value;
  }

  dealerListById(index: number, list: any) {
    return list.value;
  }
}
